import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, MessagesSquare, User, Wrench } from "lucide-react";
import PlayerVoiceRequest from "./PlayerVoiceRequest";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";

const NavButton = ({ active, onClick, icon: Icon, label, testid, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    data-active={active ? "true" : undefined}
    aria-current={active ? "page" : undefined}
    className="pc-player-bottom-nav__item flex flex-col items-center justify-center min-w-0 flex-1"
    style={{
      background: "none", border: "none", cursor: disabled ? "default" : "pointer",
      color: active ? "var(--pc-cyan)" : "var(--pc-muted)",
      opacity: disabled && !active ? 0.62 : 1,
    }}
  >
    <Icon size={20} strokeWidth={1.8} />
    <span className="text-[11px] leading-[14px] font-medium">{label}</span>
  </button>
);

export default function PlayerNavigation({ active, voiceContext, onVoiceSent, contextLabel, readOnly = false, embedded = false, hideVoice = false, hideNavigationOnMobile = false, onNavigate = null, dockedVoice = false }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [portalTarget, setPortalTarget] = useState(null);
  const [voiceActive, setVoiceActive] = useState(false);
  useEffect(() => { if (!embedded) setPortalTarget(document.body); }, [embedded]);
  const positioning = embedded ? "absolute" : "fixed";
  const go = (key, path) => onNavigate ? onNavigate(key) : nav(path);
  const navigationDisabled = readOnly && !onNavigate;
  const controls = <>
    {contextLabel && !voiceActive && (
      <div
        className="pc-player-voice-context rounded-full px-3 py-2 text-xs truncate"
        style={{
          position: positioning,
          right: 66, zIndex: 34, maxWidth: "calc(100vw - 92px)",
          color: "var(--pc-muted)", background: "var(--pc-charcoal)",
          border: "1px solid var(--pc-border)",
        }}
        data-testid="voice-context-hint"
        data-docked={dockedVoice || undefined}
        title={contextLabel}
      >
        {t("voiceSelectedContext")}
      </div>
    )}
    {!hideVoice && <PlayerVoiceRequest context={voiceContext} contextTitle={contextLabel} onSent={onVoiceSent}
      disabled={readOnly} floating onActivityChange={setVoiceActive} triggerProps={{
      className: "pc-player-voice-fab w-11 h-11 rounded-full flex items-center justify-center",
      style: {
        position: positioning,
        right: 14, zIndex: 35, border: "3px solid var(--pc-black)",
        background: "var(--pc-cyan)", color: "var(--pc-player-on-accent, #001014)",
        opacity: readOnly ? 0.48 : 1,
      },
      "data-testid": "global-microphone",
      "data-docked": dockedVoice || undefined,
    }} />}
    <nav className={`pc-player-bottom-nav ${hideNavigationOnMobile ? "hidden lg:flex" : "flex"}`}
      style={{ position: positioning, zIndex: 30 }}
      data-mobile-hidden={hideNavigationOnMobile ? "true" : undefined}
      data-testid="bottom-nav">
      <NavButton active={active === "today"} disabled={navigationDisabled} onClick={() => go("today", "/today")} icon={CalendarDays} label={t("today")} testid="nav-today" />
      <NavButton active={active === "services"} disabled={navigationDisabled} onClick={() => go("services", "/services")} icon={Wrench} label={t("serviceNav")} testid="nav-services" />
      <NavButton active={active === "requests"} disabled={navigationDisabled} onClick={() => go("requests", "/requests")} icon={MessagesSquare} label={t("requests")} testid="nav-requests" />
      <NavButton active={active === "account"} disabled={navigationDisabled} onClick={() => go("account", "/today?tab=account")} icon={User} label={t("account")} testid="nav-account" />
    </nav>
  </>;
  // Live tabs share the viewport, outside page scroll/transform contexts.
  // Embedded Care previews retain their local absolute positioning.
  return !embedded && portalTarget
    ? createPortal(<div className="pc-player-ui pc-player-navigation-layer" data-testid="player-navigation-layer">{controls}</div>, portalTarget)
    : controls;
}
